import React, { useCallback, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import * as dialog from '@constants/dialogs';
import BaseDateRangeDialog, { useDateRangeDialog } from '@shared/dialogs/base-date-range-dialog';

/**
 * A simplified, prop controlled DateRangeDialog.
 * @param {object} props - Component props.
 * @returns {JSX.Element|null} The rendered dialog or null.
 */
const DateRangeDialog = ({
  contained,
  defaultRange,
  onChange,
  onClose,
  open,
  typeLabel
}) => {
  const { start: defaultStart, end: defaultEnd, contained: defaultContained } = defaultRange || {};

  const {
    start,
    end,
    contain,
    resetToEmpty,
    handleStartChange,
    handleEndChange,
    toggleContain
  } = useDateRangeDialog({
    start: defaultStart,
    end: defaultEnd,
    contain: defaultContained,
    useGlobalDefaults: false
  });

  // Reset filters on mount
  useEffect(() => {
    resetToEmpty();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset when dialog opens
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (!prevOpenRef.current && open) {
      resetToEmpty();
    }
    prevOpenRef.current = open;
  }, [open, resetToEmpty]);

  const getFilters = useCallback(() => ({
    start: moment(start).format('YYYY-MM-DD'),
    end: moment(end).format('YYYY-MM-DD'),
    contained: contain
  }), [start, end, contain]);

  const handleCancel = useCallback(() => {
    resetToEmpty();
    if (onClose) {
      onClose(dialog.DATE_RANGE);
    }
  }, [resetToEmpty, onClose]);

  const handleApply = useCallback(() => {
    if (onChange) {
      onChange(getFilters());
    }
    if (onClose) {
      onClose(dialog.DATE_RANGE);
    }
  }, [onChange, onClose, getFilters]);

  return (
    <BaseDateRangeDialog
      isOpen={open}
      start={start}
      end={end}
      contain={contain}
      handleStartChange={handleStartChange}
      handleEndChange={handleEndChange}
      toggleContain={toggleContain}
      onCancel={handleCancel}
      onApply={handleApply}
      dataTypeDisplayName={typeLabel}
      showContain={contained}
    />
  );
};

DateRangeDialog.propTypes = {
  contained: PropTypes.bool,
  defaultRange: PropTypes.object,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  typeLabel: PropTypes.string
};

export default memo(DateRangeDialog);
